import { useDisclosure } from '@chakra-ui/react';
import React, { PropsWithChildren, useCallback, useContext, useReducer } from 'react';
import TemplateCategory from 'types/entities/template-category.entity';

interface ModalProps {
  isOpen: boolean;
	onOpen: () => void;
  onClose: () => void;
}

interface State {
	category?: TemplateCategory;
	parentCategory?: TemplateCategory;
}

interface TemplateCategoriesContextValue {
	modals: {
    category: ModalProps,
		subcategory: ModalProps
  };
	state: State,
	onCreateCategory: () => void;
	onCreateSubcategory: (category: TemplateCategory) => () => void;
	onEditSubcategory: (category: TemplateCategory, parentCategory: TemplateCategory) => () => void;
	onEditCategory: (category: TemplateCategory) => () => void;
}

const TemplateCategoriesContext = React.createContext<TemplateCategoriesContextValue | undefined>(
	undefined,
);

export const useTemplateCategoriesContext = () => {
	const value = useContext(TemplateCategoriesContext);
	if (value === undefined) {
		throw Error('useTemplateCategoriesContext can not be used outside a TemplateProvider');
	}
	return value;
};

function reducer(_, newState: State) {
  return newState;
}

export default function TemplateProvider({ children }: PropsWithChildren) {
	const [state, dispatch] = useReducer(reducer, {});

  const modalCategory = useDisclosure();
	const modalSubcategory = useDisclosure();

	const onCreateCategory = useCallback(() => {
		dispatch({});
		modalCategory.onOpen();
	}, [modalCategory]);

	const onCreateSubcategory = useCallback((parentCategory: TemplateCategory) => () => {
		dispatch({ parentCategory });
		modalSubcategory.onOpen();
	}, [modalSubcategory]);

	const onEditCategory = useCallback((category: TemplateCategory) => () => {
		dispatch({ category });
		modalCategory.onOpen();
	}, [modalCategory]);

	const onEditSubcategory = useCallback((category: TemplateCategory, parentCategory: TemplateCategory) => () => {
		dispatch({ category, parentCategory });
		modalSubcategory.onOpen();
	}, [modalSubcategory]);

	return (
		<TemplateCategoriesContext.Provider
			value={{
				modals: {
          category: modalCategory,
					subcategory: modalSubcategory
        },
				state,
				onCreateCategory,
				onCreateSubcategory,
				onEditCategory,
				onEditSubcategory,
			}}>
			{children}
		</TemplateCategoriesContext.Provider>
	);
}
